// @flow
import * as React from 'react';
import styled from 'styled-components';
import Button, {
  PrimaryButton,
  PrimaryButtonDanger,
  SecondaryButton
} from 'components/Button';
import Spinner from 'components/Spinner';
import Box from 'components/Box';
import { Body } from 'components/Text';
import { FormContextConsumer } from './FormContext';
import { colors, transition } from '../../theme';

const { useState, useEffect } = React;

export const SubmitBaseButton = ({
  children,
  qaId,
  disabled,
  secondary,
  danger,
  onClick,
  submitting,
  submitDisabled,
  size,
  ...props
}: {
  children?: React.Node,
  qaId?: string,
  disabled?: boolean,
  secondary?: boolean,
  danger?: boolean,
  onClick?: Function,
  submitting?: boolean,
  submitDisabled?: boolean,
  size?: 'small' | 'large'
}) => {
  const [clicked, setClicked] = useState(false);
  const ButtonComponent = danger
    ? PrimaryButtonDanger
    : secondary
    ? SecondaryButton
    : PrimaryButton;

  const clickHandler = e => {
    if (onClick) {
      onClick(e);
    }
    setClicked(true);
  };

  useEffect(
    () => {
      if (!submitting) {
        setClicked(false);
      }
    },
    [submitting]
  );

  return (
    <ButtonComponent
      type="submit"
      data-qa-id={qaId}
      onClick={clickHandler}
      disabled={submitting || disabled || submitDisabled}
      size={size}
      {...props}
    >
      <Box position="relative">
        <Box visibility={submitting && clicked ? 'hidden' : undefined}>
          {children}
        </Box>
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
        >
          {submitting && clicked && (
            <Spinner
              size={size === 'large' ? 'small' : 'micro'}
              label="Submitting"
            />
          )}
        </Box>
      </Box>
    </ButtonComponent>
  );
};

const StyledLink = styled(Button)`
  color: ${colors.waterBlue};
  text-decoration: underline;
  transition: all ${transition};
  &:hover,
  &:focus {
    color: ${colors.niceBlue};
  }
  ${props =>
    props.disabled &&
    `
    opacity: 0.7;
  `}
`;

const BaseLink = ({
  children,
  qaId,
  disabled,
  onClick,
  submitting,
  submitDisabled,
  ...props
}: {
  children?: React.Node,
  qaId?: string,
  disabled?: boolean,
  onClick?: Function,
  submitting: boolean,
  submitDisabled?: boolean
}) => {
  const clickHandler = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <StyledLink
      type="submit"
      data-qa-id={qaId}
      onClick={clickHandler}
      disabled={submitting || disabled || submitDisabled}
      {...props}
    >
      <Body>{children}</Body>
    </StyledLink>
  );
};

const SubmitButton = ({
  'data-qa-id': qaId,
  isSubmitting,
  ...props
}: {
  'data-qa-id'?: string,
  isSubmitting?: boolean
}) => (
  <FormContextConsumer>
    {({ fieldQaIdPrefix, submitting, submitDisabled }) => (
      <SubmitBaseButton
        qaId={
          qaId ||
          (fieldQaIdPrefix
            ? `${fieldQaIdPrefix}-submit-button`
            : 'submit-button')
        }
        submitting={submitting || isSubmitting}
        submitDisabled={submitDisabled}
        {...props}
      />
    )}
  </FormContextConsumer>
);

export const SecondarySubmitButton = ({
  'data-qa-id': qaId,
  isSubmitting,
  ...props
}: {
  'data-qa-id'?: string,
  isSubmitting?: boolean
}) => (
  <FormContextConsumer>
    {({ fieldQaIdPrefix, submitting, submitDisabled }) => (
      <SubmitBaseButton
        {...props}
        type="submit"
        qaId={fieldQaIdPrefix && qaId ? `${fieldQaIdPrefix}-${qaId}` : qaId}
        submitting={submitting || isSubmitting}
        submitDisabled={submitDisabled}
        secondary
      />
    )}
  </FormContextConsumer>
);

export const SubmitLink = ({
  'data-qa-id': qaId,
  ...props
}: {
  'data-qa-id'?: string
}) => (
  <FormContextConsumer>
    {({ fieldQaIdPrefix, submitting, submitDisabled }) => (
      <BaseLink
        qaId={fieldQaIdPrefix && qaId ? `${fieldQaIdPrefix}-${qaId}` : qaId}
        submitting={submitting}
        submitDisabled={submitDisabled}
        {...props}
      />
    )}
  </FormContextConsumer>
);

export default SubmitButton;
